import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { useNavigate, useNavigationType, useParams } from "react-router-dom"
import usePostStatsContext from '@contexts/postStatsContext'
import { localAccessors } from '@utils/localStateAccess'
import { CSSTransition } from "react-transition-group"
import { fontSize, size } from "@styles/theme"
import useFeed from '@hooks/useFeed'
import styled from "@emotion/styled"

import { PostShowReplyInput } from '@components/feeds/post/post_elements/PostShowReplyInput'
import { PiCaretLeftBold } from "react-icons/pi"
import ScreenContainer from "./ScreenContainer"
import Post from "@components/feeds/post/Post"
import { Virtuoso } from "react-virtuoso"
import { Button } from "@mui/material"
import EndDot from "@elements/EndDot"

const TopBarClearfix = styled.div`
  height: ${size(6)};
  width: 100%;
`

const TopBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: black;
  border-bottom: 1px solid rgba(255,255,255,0.1);

  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: ${size(6)};
  z-index: 2;
`

const BackButton = styled(Button)`
  color: white;
  font-size: ${fontSize('md')};
`

const PostLoader = styled.h2`
  text-align: center;
  padding-top: 6rem;
  color: #333333;
`

const ReplyLabel = styled.div`
  padding: ${size(2)};
  font-weight: 600;
`

const ReplyBarClearfix = styled.div`
  height: ${size(7)};
  width: 100%;
`

const PostNotFound = styled.div`
  border-radius: 15px;
  background-color: #141414;
  color: #333333;
  padding: ${size(8)} 0;
  margin: ${size(2)};
  margin-top: ${size(10)};
  text-align: center;
  font-weight: 600;
  font-size: ${fontSize('lg')};
`

function PostShow({ ...rest }) {
  const { id } = useParams();
  // The component remounts ONLY when the 'id' parameter changes
  return <PostShowWRapped key={id} {...rest} />;
}

const components = {
  Header: ({ context }) => {
    const { isLoadingPost, isPostNotFound, replies, post, didRenderAtPos } = context;
    if (post) return (
      <div style={{ visibility: didRenderAtPos ? 'visible' : 'hidden' }}>
        <TopBarClearfix />
        <PostShowReplyInput {...context} />
        <Post
          index={0}
          isFullScreen
          {...context}
        />
        {replies?.length > 0 && <ReplyLabel>Post Replies</ReplyLabel>}
      </div>
    );
    else if (isLoadingPost) return (
      <PostLoader>
        Loading Data...
      </PostLoader>
    ); else if (isPostNotFound) return (
      <PostNotFound>Post Not Found</PostNotFound>
    );
  },
  Footer: ({ context }) => {
    const { replies, post, replyPostsLoaded, didRenderAtPos } = context
    const renderFooterContent = () => {
      if (post && replies.length === 0 && replyPostsLoaded) return (
        <h2 style={{ textAlign: 'center', paddingTop: size(6) }}>No Replies Yet</h2>
      ); else if (post && replies?.length > 0) return (
        <>
          <EndDot style={{ paddingTop: size(10) }} />
          <ReplyBarClearfix />
        </>
      )
    }
    return (
      <div style={{
        paddingBottom: window?.screen?.height*0.75 || size(30),
        visibility: didRenderAtPos ? 'visible' : 'hidden',
      }}>
        {renderFooterContent()}
      </div>
    )
  },
}

const PostShowWRapped = () => {
  const [didRenderAtPos, setDidRenderAtPos] = useState(false)
  const { postShow, postShowReplies } = localAccessors
  const navigationType = useNavigationType()
  const postStats = usePostStatsContext()
  const navigate = useNavigate()
  const params = useParams()
  const nodeRef = useRef()
  const listRef = useRef()

  const mainPostState = useFeed({
    getPostsUrl: `api/posts/${params.id}`,
    feedAccessor: postShow(params.id),
    postStats,
  })

  const replyPostState = useFeed({
    getPostsUrl: `api/posts/${params.id}/replies`,
    feedAccessor: postShowReplies(params.id),
    postStats,
  })

  // feed state
  const {
    posts: mainPosts,
    getInitialPosts: getInitialMainPost,
    postsLoaded: mainPostsLoaded,
    postsEmpty: mainPostsEmpty,
    setPostsLoaded: setMainPostLoaded,

    setLikeToState,
    setUnlikeToState,
    setRepostToState,
    newReplyAfterCreate,
    afterPostDelete,

    feedAccessor: mainPostFeedAccessor,
  } = mainPostState
  const mainPost = mainPosts?.[0]

  // feed state
  const {
    posts: replies,
    postsLoaded: replyPostsLoaded,
    setPostsLoaded: setRepliesLoaded,

    getInitialPosts: getInitialReplies,
    setLikeToState: setLikeToStateReply,
    setUnlikeToState: setUnlikeToStateReply,
    setRepostToState: setRepostToStateReply,
    newReplyAfterCreate: newReplyAfterCreateReply,
    afterPostDelete: afterPostDeleteReply,
    addPostToFeedStart: addReplyPostToFeedStart,

    feedAccessor: replyPostFeedAccessor,

  } = replyPostState

  // get posts on initial render
  useLayoutEffect(() => {
    // Get posts from API if forward navigated
    if (navigationType !== 'POP') {
      getInitialMainPost()
      getInitialReplies()
    } else {
      // If backnavigated, posts and replies populate from session storage
      setMainPostLoaded(true)
      setRepliesLoaded(true)
    }
  }, [])
  
  // Scrolls the component to the reply-bottom when the posts are replies
  useEffect(() => {
    const replyTopHeight = mainPost?.display?.data?.replyTop?.post?.dimensions?.postTotal?.heightInPx
    if (params.id === mainPost?.uuid && replyTopHeight && !didRenderAtPos) {
      setTimeout(() => {
        listRef.current.scrollTo({ top: replyTopHeight })
        setDidRenderAtPos(true)
      })
    } else if (mainPost ) {
      setDidRenderAtPos(true)
    }
  }, [mainPost])

  const newReplyAfterCreateAndAddToReplies = (newReply, mainPostId) => {
    // We want to make sure the reply being added is actually for the current post, rather than the replyTop of the current post.
    if (newReply?.parent?.uuid === mainPostId) {
      newReply.isReplyBottomOnly = true
      addReplyPostToFeedStart(newReply)
    }

    // Call regular afterCreate callback
    newReplyAfterCreate(newReply, mainPostId)
  }

  return (
    <CSSTransition
      in
      appear
      nodeRef={nodeRef}
      timeout={300}
      classNames='animation__modal'>
      <ScreenContainer ref={nodeRef}>
        <TopBar>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BackButton onClick={() => navigate(-1)}>
              <PiCaretLeftBold size={size(2.5)} />
              <span style={{ fontSize: fontSize('xl'), fontWeight: 500, marginLeft: size(3) }}>Post</span>
            </BackButton>
          </div>
        </TopBar>
        <Virtuoso
          ref={node => { if (node) listRef.current = node  }}
          data={replies}
          overscan={window.screen.height*2}
          context={{
            post: mainPost,
            beforeLikeHook: setLikeToState,
            beforeUnlikeHook: setUnlikeToState,
            beforeRepostHook: setRepostToState,
            newReplyAfterCreate: newReplyAfterCreateAndAddToReplies,
            afterDelete: afterPostDelete,
            feedAccessor: mainPostFeedAccessor,
            didRenderAtPos,

            isLoadingPost: !mainPostsLoaded,
            isPostNotFound: mainPostsEmpty,

            replies,
            replyPostsLoaded,
          }}
          itemContent={(index, reply) => mainPost && didRenderAtPos && (
            <Post
              key={reply?.uuid || index}
              post={reply}
              beforeLikeHook={setLikeToStateReply}
              beforeUnlikeHook={setUnlikeToStateReply}
              beforeRepostHook={setRepostToStateReply}
              newReplyAfterCreate={newReplyAfterCreateReply}
              afterDelete={afterPostDeleteReply}
              feedAccessor={replyPostFeedAccessor}
            />
          )}
          components={components}
        />
      </ScreenContainer>
    </CSSTransition>
  )
}

export default PostShow
